
import { ChangeDetectionStrategy, Component, computed, inject, signal, ElementRef, effect, viewChild, OnDestroy } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SupabaseService } from '../../services/supabase.service';
import { ServiceManagerService } from '../../services/service-manager.service';
import { ApplicationDetail, ApplicationDocument, OnboardingApplication, OnboardingWebhook } from '../../models/application';
import { AuthService } from '../../services/auth.service';
import { AppUser } from '../../models/user';
import { ContactDetailsUpdate } from '../../models/contact-details-update.model';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader.service';
import { DomSanitizer } from '@angular/platform-browser';
import { RealtimeChannel } from '@supabase/supabase-js';

declare const google: any;

// Add `cv` to the global window object for OpenCV.js
declare global {
  interface Window {
    cv: any;
  }
}

type ActionType = 'approved' | 'rejected' | null;

declare var L: any;

@Component({
  selector: 'app-application-detail',
  templateUrl: './application-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, FormsModule, TitleCasePipe],
})
export class ApplicationDetailComponent implements OnDestroy {
  private route: ActivatedRoute;
  private router = inject(Router);
  private supabaseService = inject(SupabaseService);
  private authService = inject(AuthService);
  private serviceManagerService = inject(ServiceManagerService);
  private googleMapsLoaderService = inject(GoogleMapsLoaderService);
  sanitizer = inject(DomSanitizer);

  application = signal<ApplicationDetail | null>(null);
  allUsers = signal<AppUser[]>([]);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  actionInProgress = signal<boolean>(false);
  revertingApproved = signal<boolean>(false);

  // Rejection Modal state
  showRejectionModal = signal<boolean>(false);
  modalAction = signal<ActionType>(null);
  rejectionReason = signal<string>('');

  // Approval Modal state
  showApprovalModal = signal<boolean>(false);
  approvalNotes = signal<string>('');
  logonDomain = signal<string>('');
  accountShortName = signal<string>('');
  existingSystemDetails = signal<{logon_domain: string, account_short_name: string}[]>([]);
  logonDomainError = signal<string | null>(null);
  accountShortNameError = signal<string | null>(null);
  approvalError = signal<string | null>(null);

  // Rollback Modal state
  showRollbackModal = signal<boolean>(false);
  rollbackError = signal<string | null>(null);

  // Suspend Modal state
  showSuspendModal = signal<boolean>(false);
  suspendResult = signal<{ ok: boolean; message: string } | null>(null);

  // Reinstate Modal state
  showReinstateModal = signal<boolean>(false);
  reinstateResult = signal<{ ok: boolean; message: string } | null>(null);

  // Send legal documents state
  showLegalDocsConfirm = signal<boolean>(false);
  sendingLegalDocs = signal<boolean>(false);
  sendLegalDocsSuccess = signal<boolean>(false);
  sendLegalDocsError = signal<string | null>(null);

  // Portal credentials state
  portalCredentials = signal<{ username: string; tempPassword: string } | null>(null);
  showPortalCredentials = signal(false);
  portalCredentialsLoading = signal(false);
  portalCredentialsError = signal<string | null>(null);
  copiedField = signal<string | null>(null);

  // Customer validation state
  validationStatus = signal<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
  validationError  = signal<string | null>(null);

  // Edit contact details state
  isEditingContact = signal(false);
  editContactForm = signal<ContactDetailsUpdate | null>(null);
  editAddressInputText = signal('');
  isSavingContact = signal(false);
  saveContactError = signal<string | null>(null);
  editAddressInputEl = viewChild<ElementRef>('editAddressInput');

  // Document viewer state
  viewingDocument = signal<ApplicationDocument | null>(null);
  documentUrl = signal<string | null>(null);
  documentUrlLoading = signal<boolean>(false);
  documentExpiryDate = signal<string>('');
  isApprovingDocument = signal<boolean>(false);
  documentApprovalError = signal<string | null>(null);

  // Map state
  mapContainer = viewChild<ElementRef>('map');
  private map?: any;

  // Image editing state
  docCanvas = viewChild<ElementRef<HTMLCanvasElement>>('docCanvas');
  cvReady = signal(false);
  private cvPollInterval: any;
  private originalImage = signal<HTMLImageElement | null>(null);
  detectedCorners = signal<{x: number, y: number}[] | null>(null);
  isCropped = signal(false);

  // Crop save/undo state
  private preCropImageSrc = signal<string | null>(null);
  isSaving = signal(false);
  saveError = signal<string | null>(null);

  // Drag interaction state
  private draggingCornerIndex = signal<number | null>(null);
  private hoveredCornerIndex = signal<number | null>(null);
  private readonly CORNER_HIT_RADIUS = 16;
  private readonly CORNER_DRAW_RADIUS = 10;

  // Manual corner selection state
  manualCornerMode = signal(false);
  manualCornerPoints = signal<{x: number, y: number}[]>([]);

  canvasCursor = computed(() => {
    if (this.manualCornerMode()) return 'crosshair';
    if (this.draggingCornerIndex() !== null) return 'grabbing';
    if (this.hoveredCornerIndex() !== null) return 'grab';
    return 'default';
  });

  applicationId: string | null;
  reviewer = this.authService.currentUser;
  private channels: RealtimeChannel[] = [];

  private documentTypeMap: { [key: string]: string } = {
    'publicliabilityinsurance': 'Public Liability Insurance',
    'operatorslicence': 'Operators Licence',
    'employersliabilityinsurance': 'Employers Liability Insurance',
    'public_liability_insurance': 'Public Liability Insurance',
    'operators_licence': 'Operators Licence',
    'employers_liability_insurance': 'Employers Liability Insurance'
  };

  private usersMap = computed(() => {
    const map = new Map<string, string>();
    for (const user of this.allUsers()) {
        map.set(user.id, user.full_name || 'Unknown User');
    }
    return map;
  });

  isExpiryDateValid = computed(() => {
    const dateStr = this.documentExpiryDate();
    if (!dateStr) return false;
    
    // An input type="date" returns 'YYYY-MM-DD'.
    // To avoid timezone issues where new Date('YYYY-MM-DD') can be interpreted
    // as the previous day in some timezones, we construct the date from its parts,
    // which correctly uses the local timezone at midnight.
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // The month is 0-indexed in the JavaScript Date constructor (0 for January).
    const expiry = new Date(year, month - 1, day);

    const today = new Date();
    // Set today to midnight for a fair comparison.
    today.setHours(0, 0, 0, 0);

    return !isNaN(expiry.getTime()) && expiry >= today;
  });

  enhancedDocuments = computed(() => {
    const docs = this.application()?.documents ?? [];
    return docs.map(doc => ({
      ...doc,
      status: doc.expiry_date ? 'approved' : 'pending'
    }));
  });

  allDocumentsApproved = computed(() => {
    const docs = this.enhancedDocuments();
    if (!docs || docs.length === 0) {
      return true; 
    }
    return docs.every(doc => doc.status === 'approved');
  });

  viewingEnhancedDocument = computed(() => {
    const viewingDoc = this.viewingDocument();
    if (!viewingDoc) {
      return null;
    }
    return this.enhancedDocuments().find(d => d.id === viewingDoc.id) ?? null;
  });

  enhancedLogs = computed(() => {
    const logs = this.application()?.logs ?? [];
    const userMap = this.usersMap();
    const currentReviewer = this.reviewer();
    return logs.map(log => {
      const actor_name = userMap.get(log.actor_user_id)
        || (currentReviewer?.id === log.actor_user_id ? currentReviewer.fullName : undefined)
        || `Unknown (${log.actor_user_id.substring(0, 8)}...)`;

      return {
        ...log,
        actor_name
      };
    });
  });

  mergedApplicationData = computed(() => {
    const app = this.application();
    if (!app || !app.data || app.data.length === 0) {
      return null;
    }
    return app.data.reduce((acc, curr) => {
        if (curr && typeof curr === 'object') {
            return { ...acc, ...curr };
        }
        return acc;
    }, {});
  });
  
  formattedAddress = computed(() => {
    const data = this.mergedApplicationData();
    if (!data) return 'Not provided';

    const parts = [
      data['address_line1'],
      data['address_line2'],
      data['address_city'],
      data['address_county'],
      data['address_postcode']
    ].filter(part => !!part);

    if (parts.length === 0) return 'Not provided';
    return parts.join(', ');
  });
  
  canEditContact = computed(() => {
    const status = this.application()?.status;
    return status === 'submitted' || status === 'rejected';
  });

  validationRequired = computed(() => {
    const status = this.application()?.status;
    return status === 'submitted' || status === 'in_review';
  });

  canStartReview = computed(() => this.validationStatus() === 'valid');

  canApprove = computed(() => this.validationStatus() === 'valid');

  latestWebhook = computed<OnboardingWebhook | null>(() => {
    const webhooks = this.application()?.webhooks;
    if (!webhooks || webhooks.length === 0) {
      return null;
    }
    // The service already orders webhook records by creation date descending.
    return webhooks[0];
  });

  buildStatus = computed(() => {
    const webhook = this.latestWebhook();
    if (!webhook) {
      return { text: 'Awaiting Build', class: 'bg-slate-100 text-slate-800' };
    }
    switch (webhook.status) {
      case 'sent': return { text: 'Build Complete', class: 'bg-green-100 text-green-800' };
      case 'in_progress': return { text: 'Build In Progress', class: 'bg-cyan-100 text-cyan-800 animate-pulse' };
      case 'processing': return { text: 'Build In Progress', class: 'bg-cyan-100 text-cyan-800 animate-pulse' };
      case 'pending': return { text: 'Build Pending', class: 'bg-yellow-100 text-yellow-800' };
      case 'failed': return { text: 'Build Failed', class: 'bg-red-100 text-red-800' };
      default: return { text: 'Awaiting Build', class: 'bg-slate-100 text-slate-800' };
    }
  });

  isBuildFailed = computed(() => {
    const webhook = this.latestWebhook();
    return webhook?.status === 'failed';
  });

  isBuildComplete = computed(() => {
    const webhook = this.latestWebhook();
    return webhook?.status === 'sent';
  });

  constructor() {
    this.route = inject(ActivatedRoute);
    this.applicationId = this.route.snapshot.paramMap.get('id');
    
    if (this.applicationId) {
      this.loadApplicationDetails(this.applicationId);
      const id = this.applicationId;
      this.channels.push(
        this.supabaseService.subscribeToTableChanges(
          'onboarding_applications',
          'public',
          `application-detail-${id}`,
          (payload) => {
            if (payload.eventType !== 'DELETE' && payload.new.id === id) {
              this.loadApplicationDetails(id);
            }
          }
        )
      );
      this.channels.push(
        this.supabaseService.subscribeToTableChanges(
          'onboarding_webhook_queue',
          'public',
          `webhook-detail-${id}`,
          (payload) => {
            if (payload.eventType !== 'DELETE' && payload.new.application_id === id) {
              this.loadApplicationDetails(id);
            }
          }
        )
      );
      this.channels.push(
        this.supabaseService.subscribeToTableChanges(
          'onboarding_application_log',
          'public',
          `log-detail-${id}`,
          (payload) => {
            if (payload.eventType !== 'DELETE' && payload.new.application_id === id) {
              this.loadApplicationDetails(id);
            }
          }
        )
      );
    } else {
      this.error.set('No application ID provided.');
      this.loading.set(false);
    }

    // Poll for OpenCV.js to be ready
    this.cvPollInterval = setInterval(() => {
      if (window.cv) {
        this.cvReady.set(true);
        clearInterval(this.cvPollInterval);
        console.log('[OpenCV] OpenCV.js is ready.');
      }
    }, 200);

    effect((onCleanup) => {
      const mapEl = this.mapContainer();
      const appData = this.mergedApplicationData();
      
      if (mapEl && appData && appData['latitude'] && appData['longitude']) {
          this.initMap(mapEl.nativeElement, appData['latitude'], appData['longitude']);
      }

      onCleanup(() => {
        if (this.map) {
          this.map.remove();
          this.map = undefined;
        }
      });
    });

    // Effect to wire up Google Maps autocomplete when edit mode is active.
    effect((onCleanup) => {
      const inputEl = this.editAddressInputEl()?.nativeElement;
      if (!this.isEditingContact() || !inputEl || !this.googleMapsLoaderService.isLoaded()) return;

      const ac = new google.maps.places.Autocomplete(inputEl, {
        types: ['address'],
        componentRestrictions: { country: 'gb' },
        fields: ['address_components', 'geometry.location', 'formatted_address'],
      });

      const listener = ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place?.address_components && place?.geometry?.location) {
          const parsed = this.parseAddressComponents(place);
          this.editContactForm.update(f => f ? {
            ...f,
            address_line1:    parsed.line1,
            address_line2:    parsed.line2,
            address_city:     parsed.city,
            address_county:   parsed.county,
            address_postcode: parsed.postcode,
            latitude:         parsed.latitude,
            longitude:        parsed.longitude,
          } : f);
          this.editAddressInputText.set(place.formatted_address || '');
        }
      });

      onCleanup(() => {
        google.maps.event.clearInstanceListeners(ac);
        document.querySelectorAll('.pac-container').forEach(c => c.remove());
      });
    });

    // Effect to draw image on canvas when both are ready.
    effect(() => {
      const image = this.originalImage();
      const canvasViewChild = this.docCanvas();
      const doc = this.viewingDocument();
      const corners = this.detectedCorners();
      const isDragging = this.draggingCornerIndex();

      // Skip redraw when dragging — the mousemove/touchmove handler redraws directly for smooth 60fps.
      if (isDragging !== null) return;

      if (image && canvasViewChild && doc && !this.isPdf(doc.storage_path)) {
        console.log('[Effect] Image and canvas are ready. Drawing image.');
        this.drawAndFitImage();
        if (corners) {
            this.drawCorners(corners);
        }
      }
    });
  }
  
  // --- Edit Contact Details ---

  updateEditField(field: keyof ContactDetailsUpdate, value: string) {
    this.editContactForm.update(f => {
      if (!f) return f;
      return { ...f, [field]: value };
    });
  }

  startEditContact() {
    const data = this.mergedApplicationData();
    if (!data) return;
    this.editContactForm.set({
      fleet_name:       data['fleet_name']       ?? '',
      address_line1:    data['address_line1']    ?? '',
      address_line2:    data['address_line2']    ?? '',
      address_city:     data['address_city']     ?? '',
      address_county:   data['address_county']   ?? '',
      address_postcode: data['address_postcode'] ?? '',
      latitude:         data['latitude']         ?? null,
      longitude:        data['longitude']        ?? null,
      contact_name:     data['contact_name']     ?? '',
      contact_email:    data['contact_email']    ?? '',
      contact_phone:    data['contact_phone']    ?? '',
      fleet_phone:      data['fleet_phone']      ?? '',
    });
    this.editAddressInputText.set(this.formattedAddress());
    this.saveContactError.set(null);
    this.isEditingContact.set(true);
    this.googleMapsLoaderService.load();
  }

  cancelEditContact() {
    this.isEditingContact.set(false);
    this.editContactForm.set(null);
    this.saveContactError.set(null);
    document.querySelectorAll('.pac-container').forEach(c => c.remove());
  }

  async saveContactDetails() {
    const form = this.editContactForm();
    if (!this.applicationId || !form) return;

    this.isSavingContact.set(true);
    this.saveContactError.set(null);

    try {
      await this.supabaseService.updateApplicationContactDetails(this.applicationId, form);
      await this.loadApplicationDetails(this.applicationId);
      this.cancelEditContact();
      // Reset validation — fleet phone or name may have changed
      if (this.validationStatus() !== 'idle') {
        this.validationStatus.set('idle');
        this.validationError.set(null);
      }
    } catch (e: any) {
      this.saveContactError.set(e.message || 'Failed to save contact details.');
    } finally {
      this.isSavingContact.set(false);
    }
  }

  async togglePortalCredentials() {
    if (this.showPortalCredentials()) {
      this.showPortalCredentials.set(false);
      return;
    }
    if (this.portalCredentials()) {
      this.showPortalCredentials.set(true);
      return;
    }
    this.portalCredentialsLoading.set(true);
    this.portalCredentialsError.set(null);
    try {
      const creds = await this.serviceManagerService.fetchFleetTempPassword(
        this.mergedApplicationData(),
        this.application()?.system_details ?? null
      );
      this.portalCredentials.set(creds);
      this.showPortalCredentials.set(true);
    } catch (e: any) {
      this.portalCredentialsError.set(e.message || 'Failed to retrieve credentials. Please try again.');
    } finally {
      this.portalCredentialsLoading.set(false);
    }
  }

  copyToClipboard(value: string, field: string = '') {
    navigator.clipboard.writeText(value);
    if (field) {
      this.copiedField.set(field);
      setTimeout(() => this.copiedField.set(null), 1800);
    }
  }

  async validateCustomer() {
    if (!this.applicationId || !this.validationRequired()) return;

    const data = this.mergedApplicationData();
    const fleetPhone = data?.['fleet_phone'] ?? '';
    const fleetName  = data?.['fleet_name']  ?? '';

    this.validationStatus.set('loading');
    this.validationError.set(null);

    try {
      const valid = await this.serviceManagerService.validateCustomer(fleetPhone, fleetName);
      this.validationStatus.set(valid ? 'valid' : 'invalid');
    } catch (e: any) {
      this.validationStatus.set('idle');
      this.validationError.set(e.message || 'Validation request failed. Please try again.');
    }
  }

  private parseAddressComponents(place: any): { line1: string; line2: string; city: string; county: string; postcode: string; latitude: number | null; longitude: number | null } {
    const result = { line1: '', line2: '', city: '', county: '', postcode: '', latitude: null as number | null, longitude: null as number | null };
    if (place.geometry?.location) {
      result.latitude  = place.geometry.location.lat();
      result.longitude = place.geometry.location.lng();
    }
    const map: { [key: string]: string } = {};
    place.address_components?.forEach((c: any) => {
      c.types.forEach((t: string) => { map[t] = c.long_name; });
    });
    const streetNumber = map['street_number'] || '';
    const route        = map['route']         || '';
    result.line1 = `${streetNumber} ${route}`.trim();
    if (!result.line1 && (map['premise'] || map['subpremise'])) {
      result.line1 = `${map['premise'] || ''} ${map['subpremise'] || ''}`.trim();
    }
    result.city    = map['postal_town'] || map['locality'] || '';
    result.county  = map['administrative_area_level_2'] || '';
    result.postcode = map['postal_code'] || '';
    return result;
  }

  ngOnDestroy() {
    if (this.cvPollInterval) {
      clearInterval(this.cvPollInterval);
    }
    this.channels.forEach(channel => this.supabaseService.unsubscribeFromChannel(channel));
  }

  private initMap(element: HTMLElement, lat: number, lng: number): void {
    if (this.map) return;
    this.map = L.map(element).setView([lat, lng], 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map);
    L.marker([lat, lng]).addTo(this.map)
      .bindPopup(this.mergedApplicationData()?.['fleet_name'] || 'Fleet Location');
    setTimeout(() => { this.map?.invalidateSize(); }, 0);
  }

  async loadApplicationDetails(id: string) {
    this.loading.set(true);
    this.error.set(null);
    try {
      const data = await this.supabaseService.getApplicationDetails(id);
      if (data) {
        this.application.set(data);
      } else {
        this.error.set('Application not found.');
        this.loading.set(false);
        return;
      }
      try {
        const users = await this.supabaseService.getUsers();
        this.allUsers.set(users);
      } catch (userError) {
        console.warn('Could not fetch user list for log name resolution. History will show User IDs instead.', userError);
        this.allUsers.set([]);
      }
    } catch (e) {
      this.error.set('Failed to load application details.');
      console.error(e);
    } finally {
      this.loading.set(false);
    }
  }
  
  async startReview() {
    if (!this.applicationId || !this.canStartReview()) return;
    this.actionInProgress.set(true);
    const success = await this.updateStatus('in_review', 'Review started.');
    if (success) {
      // Data will be reloaded via realtime subscription
    }
    this.actionInProgress.set(false);
  }

  async returnToStatus(newStatus: 'submitted' | 'in_review') {
    const reviewer = this.reviewer();
    if (!this.applicationId || !reviewer) return;
    this.actionInProgress.set(true);
    try {
      const success = await this.supabaseService.reopenApplication(this.applicationId, newStatus);
      if (success) {
        await this.supabaseService.addApplicationLog({
          application_id: this.applicationId,
          actor_user_id: reviewer.id,
          action: `Status returned to ${newStatus.replace('_', ' ')}`,
          description: `Application returned to ${newStatus.replace('_', ' ')} by reviewer.`
        });
        if (newStatus === 'submitted') {
          this.validationStatus.set('idle');
          this.validationError.set(null);
        }
      }
    } catch (e: any) {
      this.error.set(e.message || 'Failed to update status.');
    } finally {
      this.actionInProgress.set(false);
    }
  }

  async openActionModal(action: ActionType) {
    if (action === 'approved') {
        await this.prepareAndShowApprovalModal();
    } else { // 'rejected'
        this.modalAction.set(action);
        this.rejectionReason.set('');
        this.showRejectionModal.set(true);
    }
  }

  closeRejectionModal() {
    this.showRejectionModal.set(false);
    this.modalAction.set(null);
  }

  async submitRejection() {
    const reviewer = this.reviewer();
    const action = this.modalAction();
    if (!this.applicationId || action !== 'rejected' || !this.rejectionReason() || !reviewer) return;

    this.actionInProgress.set(true);
    
    const success = await this.supabaseService.updateApplicationDecision(this.applicationId, action, this.rejectionReason());
    
    if (success) {
      await this.supabaseService.addApplicationLog({
        application_id: this.applicationId,
        actor_user_id: reviewer.id,
        action: `Application ${action}`,
        description: this.rejectionReason()
      });
      this.closeRejectionModal();
      // Data will be reloaded via realtime subscription
    }
    this.actionInProgress.set(false);
  }

  async prepareAndShowApprovalModal() {
    this.actionInProgress.set(true);
    this.approvalError.set(null);
    try {
        // If this application already has system details linked (e.g. it was
        // previously approved, a build failed, and it was returned to submitted
        // for edits), reuse those existing values so re-approval updates the
        // same system_details record (preserving its manager_id) instead of
        // suggesting a brand-new one.
        const existingOwnDetails = this.application()?.system_details ?? null;

        const details = await this.supabaseService.getSystemDetails(existingOwnDetails?.id);
        this.existingSystemDetails.set(details);

        if (existingOwnDetails) {
          this.logonDomain.set(existingOwnDetails.logon_domain);
          this.accountShortName.set(existingOwnDetails.account_short_name);
        } else {
          const fleetName = this.mergedApplicationData()?.['fleet_name'] || 'NewFleet';
          this.logonDomain.set(this.generateUniqueLogonDomain(fleetName));
          this.accountShortName.set(this.generateUniqueAccountShortName(fleetName));
        }
        this.validateLogonDomain();
        this.validateAccountShortName();

        this.approvalNotes.set('');
        this.showApprovalModal.set(true);
    } catch (e: any) {
        this.error.set(e.message || 'Failed to prepare approval step.');
    } finally {
        this.actionInProgress.set(false);
    }
  }

  closeApprovalModal() {
    this.showApprovalModal.set(false);
  }

  openRollbackModal(): void {
    this.rollbackError.set(null);
    this.showRollbackModal.set(true);
  }

  closeRollbackModal(): void {
    this.showRollbackModal.set(false);
  }

  async confirmRollback(): Promise<void> {
    const application = this.application();
    if (!application || !this.applicationId) return;

    this.actionInProgress.set(true);
    this.rollbackError.set(null);
    try {
      const job = await this.serviceManagerService.triggerRollback(application, this.mergedApplicationData());
      if (job.status === 'Failed') {
        this.rollbackError.set(job.errorMessage || 'Rollback failed in ServiceManager. Check the job log before retrying.');
        return;
      }
      await this.supabaseService.deleteApplication(this.applicationId);
      this.router.navigate(['/applications']);
    } catch (e: any) {
      this.rollbackError.set(e.message || 'Rollback failed or timed out.');
    } finally {
      this.actionInProgress.set(false);
    }
  }

  openSuspendModal(): void {
    this.suspendResult.set(null);
    this.showSuspendModal.set(true);
  }

  closeSuspendModal(): void {
    this.showSuspendModal.set(false);
  }

  async confirmSuspend(): Promise<void> {
    const reviewer = this.reviewer();
    if (!this.applicationId || !reviewer) return;

    this.actionInProgress.set(true);
    this.suspendResult.set(null);
    try {
      const success = await this.updateStatus('suspended', 'System suspended by reviewer.');
      if (!success) {
        this.error.set('Failed to suspend application.');
        return;
      }

      const job = await this.serviceManagerService.notifyApplicationSuspend(
        this.applicationId,
        this.mergedApplicationData(),
        this.application()?.system_details ?? null
      );
      this.closeSuspendModal();
      // Data will be reloaded via realtime subscription
      this.suspendResult.set(
        job.status === 'Failed'
          ? { ok: false, message: 'Suspend request accepted, but ServiceManager reported failure. Review job steps.' }
          : { ok: true, message: 'System suspended successfully.' }
      );
      setTimeout(() => this.suspendResult.set(null), 6000);
    } catch (e: any) {
      this.error.set(e.message || 'An error occurred while suspending the application.');
    } finally {
      this.actionInProgress.set(false);
    }
  }

  openReinstateModal(): void {
    this.reinstateResult.set(null);
    this.showReinstateModal.set(true);
  }

  closeReinstateModal(): void {
    this.showReinstateModal.set(false);
  }

  async confirmReinstate(): Promise<void> {
    const reviewer = this.reviewer();
    if (!this.applicationId || !reviewer) return;

    this.actionInProgress.set(true);
    this.reinstateResult.set(null);
    try {
      const success = await this.updateStatus('live', 'System reinstated by reviewer.');
      if (!success) {
        this.error.set('Failed to reinstate application.');
        return;
      }

      const job = await this.serviceManagerService.notifyApplicationReinstate(
        this.applicationId,
        this.mergedApplicationData(),
        this.application()?.system_details ?? null
      );
      this.closeReinstateModal();
      // Data will be reloaded via realtime subscription
      this.reinstateResult.set(
        job.status === 'Failed'
          ? { ok: false, message: 'Reinstate request accepted, but ServiceManager reported failure. Review job steps.' }
          : { ok: true, message: 'System reinstated successfully.' }
      );
      setTimeout(() => this.reinstateResult.set(null), 6000);
    } catch (e: any) {
      this.error.set(e.message || 'An error occurred while reinstating the application.');
    } finally {
      this.actionInProgress.set(false);
    }
  }

  async submitApproval() {
    this.validateLogonDomain();
    this.validateAccountShortName();
    if (this.logonDomainError() || this.accountShortNameError() || !this.approvalNotes()) {
        if (!this.approvalNotes()) this.approvalError.set('Approval notes are required.');
        return;
    }

    const reviewer = this.reviewer();
    if (!this.applicationId || !reviewer) return;

    this.actionInProgress.set(true);
    this.approvalError.set(null);

    try {
        await this.supabaseService.approveApplicationWithSystemDetails(
            this.applicationId,
            this.approvalNotes(),
            this.logonDomain(),
            this.accountShortName()
        );

        await this.supabaseService.addApplicationLog({
            application_id: this.applicationId,
            actor_user_id: reviewer.id,
            action: `Application approved`,
            description: `System details created. Notes: ${this.approvalNotes()}`
        });

        this.closeApprovalModal();
        // Data will be reloaded via realtime subscription

    } catch (e: any) {
        this.approvalError.set(e.message || 'An unexpected error occurred.');
        console.error(e);
    } finally {
        this.actionInProgress.set(false);
    }
  }

  private generateUniqueLogonDomain(name: string): string {
      const existing = this.existingSystemDetails().map(d => d.logon_domain.toLowerCase());
      let base = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (base.length === 0) base = 'domain';

      let finalName = `${base}.com`;
      let counter = 2;
      while (existing.includes(finalName)) {
          finalName = `${base}${counter}.com`;
          counter++;
      }
      return finalName;
  }

  private generateUniqueAccountShortName(name: string): string {
      const existing = this.existingSystemDetails().map(d => d.account_short_name.toUpperCase());
      let base = name.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 8);
      if (base.length === 0) base = 'ACCOUNT';

      let finalName = base;
      let counter = 2;
      while (existing.includes(finalName)) {
          finalName = `${base}${counter}`;
          counter++;
      }
      return finalName;
  }

  validateLogonDomain() {
      const domain = this.logonDomain();
      if (!domain) {
          this.logonDomainError.set('Logon Domain is required.');
          return;
      }
      if (this.existingSystemDetails().some(d => d.logon_domain.toLowerCase() === domain.toLowerCase())) {
          this.logonDomainError.set('This Logon Domain is already in use.');
      } else {
          this.logonDomainError.set(null);
      }
  }

  validateAccountShortName() {
      const shortName = this.accountShortName();
      if (!shortName) {
          this.accountShortNameError.set('Account Short Name is required.');
          return;
      }
      if (shortName.length > 16) {
          this.accountShortNameError.set('Account Short Name must be 16 characters or fewer.');
          return;
      }
      if (this.existingSystemDetails().some(d => d.account_short_name.toUpperCase() === shortName.toUpperCase())) {
          this.accountShortNameError.set('This Account Short Name is already in use.');
      } else {
          this.accountShortNameError.set(null);
      }
  }

  private async updateStatus(status: OnboardingApplication['status'], details: string): Promise<boolean> {
    const reviewer = this.reviewer();
    if (!this.applicationId || !reviewer) return false;
    
    const statusSuccess = await this.supabaseService.updateApplicationStatus(this.applicationId, status);
    if (statusSuccess) {
      await this.supabaseService.addApplicationLog({
        application_id: this.applicationId,
        actor_user_id: reviewer.id,
        action: `Status changed to ${status}`,
        description: details
      });
      return true;
    }
    return false;
  }

  async advanceStatus(newStatus: OnboardingApplication['status']) {
    const reviewer = this.reviewer();
    if (!this.applicationId || !reviewer) return;

    this.actionInProgress.set(true);

    const descriptionMap: Partial<Record<OnboardingApplication['status'], string>> = {
      ready_for_go_live: 'Application marked as ready for go-live.',
      live: 'Application is now live.'
    };
    const description = descriptionMap[newStatus] || `Status updated to ${newStatus}`;
    
    try {
      const statusSuccess = await this.supabaseService.updateApplicationStatus(this.applicationId, newStatus);
      if (statusSuccess) {
        await this.supabaseService.addApplicationLog({
          application_id: this.applicationId,
          actor_user_id: reviewer.id,
          action: `Status changed to ${newStatus.replace(/_/g, ' ')}`,
          description: description
        });
        if (newStatus === 'live') {
          this.serviceManagerService.notifyApplicationGoLive(
            this.applicationId,
            this.mergedApplicationData(),
            this.application()?.system_details ?? null
          );
        }
        // Realtime will update the UI
      } else {
        this.error.set('Failed to update status.'); // Maybe a more specific error
      }
    } catch(e: any) {
        this.error.set(e.message || 'An error occurred while updating status.');
    } finally {
        this.actionInProgress.set(false);
    }
  }

  formatDocumentType(type: string): string {
    if (!type) return 'Document';
    const lowerType = type.toLowerCase();
    if (this.documentTypeMap[lowerType]) {
      return this.documentTypeMap[lowerType];
    }
    return type.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return date.toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'
        });
    }
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  getStatusClass(status: OnboardingApplication['status'] | undefined): string {
    if(!status) return 'bg-slate-100 text-slate-800';
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'ready_for_go_live': return 'bg-cyan-100 text-cyan-800';
      case 'live': return 'bg-lime-100 text-lime-800';
      case 'suspended': return 'bg-orange-100 text-orange-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'in_review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  }

  async openDocumentViewer(doc: ApplicationDocument) {
    this.documentExpiryDate.set('');
    this.documentApprovalError.set(null);
    this.viewingDocument.set(doc);
    this.documentUrlLoading.set(true);
    this.documentUrl.set(null);
    this.resetImageState();

    try {
        const result = await this.supabaseService.createSignedUrl(doc);
        if (result) {
            this.documentUrl.set(result.signedUrl);
            if (!this.isPdf(doc.storage_path)) {
              this.loadImageToCanvas(result.signedUrl);
            }
        } else {
            console.error("Failed to get signed URL for document:", doc.id);
            this.documentUrl.set(null);
        }
    } catch (e) {
        console.error("Error fetching signed URL:", e);
        this.documentUrl.set(null);
    } finally {
        this.documentUrlLoading.set(false);
    }
  }

  closeDocumentViewer() {
    this.viewingDocument.set(null);
    this.documentUrl.set(null);
    this.originalImage.set(null);
  }

  async approveDocument() {
    const doc = this.viewingDocument();
    const reviewer = this.reviewer();
    const expiryDate = this.documentExpiryDate();

    if (!doc || !reviewer || !this.isExpiryDateValid()) return;
    this.isApprovingDocument.set(true);
    this.documentApprovalError.set(null);

    try {
      const updateSuccess = await this.supabaseService.updateApplicationDocument(doc.id, { expiry_date: expiryDate });
      
      if (updateSuccess) {
        const wasSubmitted = this.application()?.status === 'submitted';

        // Log the approval.
        this.supabaseService.addApplicationLog({
          application_id: this.applicationId!,
          actor_user_id: reviewer.id,
          action: 'Document Approved',
          description: `'${this.formatDocumentType(doc.document_type)}' was approved.`
        });
        
        // If this was the first action, move the application to 'in_review'.
        if (wasSubmitted) {
          this.updateStatus('in_review', `Document '${this.formatDocumentType(doc.document_type)}' approved.`);
        }

        this.closeDocumentViewer();
        // Data will be reloaded via realtime subscription
      } else {
        this.documentApprovalError.set('Failed to save approval. This is likely due to a missing database permission (Row Level Security policy) for updating documents. Please check your Supabase dashboard.');
      }
    } catch (e) {
      console.error('Error approving document:', e);
      this.documentApprovalError.set('An unexpected error occurred. Please check the browser console for details.');
    } finally {
      this.isApprovingDocument.set(false);
    }
  }

  isPdf(storagePath: string): boolean {
    return storagePath?.toLowerCase().endsWith('.pdf');
  }
  
  // --- Image Editing Methods ---

  private loadImageToCanvas(url: string) {
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.onload = () => {
      this.originalImage.set(image);
    };
    image.src = url;
  }

  private calculateMedian(mat: any): number {
    const cv = window.cv;
    let channels = new cv.MatVector();
    let imageVec = new cv.MatVector();
    let hist = new cv.Mat();
    let mask = new cv.Mat();
    
    try {
      cv.split(mat, channels);
      let channel = channels.get(0);
      imageVec.push_back(channel);
      
      const histSize = [256];
      const ranges = [0, 256];
      cv.calcHist(imageVec, [0], mask, hist, histSize, ranges, false);
      
      const totalPixels = mat.rows * mat.cols;
      const medianIndex = totalPixels / 2;
      let count = 0;
      let median = 0;
      for (let i = 0; i < 256; i++) {
          count += hist.data32F[i];
          if (count >= medianIndex) {
              median = i;
              break;
          }
      }
      return median;
    } finally {
      if (channels && !channels.isDeleted()) channels.delete();
      if (imageVec && !imageVec.isDeleted()) imageVec.delete();
      if (hist && !hist.isDeleted()) hist.delete();
      if (mask && !mask.isDeleted()) mask.delete();
    }
  }

  detectDocumentEdges() {
    console.log('[Edge Detection] Starting detection process...');

    if (!this.cvReady() || !window.cv) {
      console.error('[Edge Detection] ABORT: OpenCV.js is not loaded or ready.');
      alert('The image processing library has not loaded yet. Please wait a moment and try again.');
      return;
    }
    const canvas = this.docCanvas()?.nativeElement;
    if (!canvas) {
      console.error('[Edge Detection] ABORT: Canvas or image data is missing.');
      return;
    }
    console.log('[Edge Detection] Prerequisites met.');

    const cv = window.cv;
    let src: any = null, gray: any = null, blurred: any = null, canny: any = null;
    let contours: any = null, hierarchy: any = null;
    const tempMats: any[] = [];

    try {
        src = cv.imread(canvas);
        gray = new cv.Mat();
        blurred = new cv.Mat();
        canny = new cv.Mat();

        const imageArea = src.rows * src.cols;
        const minDocArea = imageArea * 0.05;

        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY, 0);
        cv.GaussianBlur(gray, blurred, new cv.Size(7, 7), 0);

        const median = this.calculateMedian(gray);

        // Multiple Canny threshold ranges: tight to loose
        const thresholdPairs: [number, number][] = [
          [Math.max(0, 0.66 * median), Math.min(255, 1.33 * median)],
          [Math.max(0, 0.5 * median),  Math.min(255, 1.5 * median)],
          [Math.max(0, 0.33 * median), Math.min(255, 1.66 * median)],
          [30, 100],
        ];

        let bestPoints: {x: number, y: number}[] | null = null;
        let bestArea = 0;

        for (const [lo, hi] of thresholdPairs) {
          cv.Canny(blurred, canny, lo, hi);

          contours = new cv.MatVector();
          hierarchy = new cv.Mat();
          cv.findContours(canny, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

          // Check all contours for valid document quads, not just the largest
          for (let i = 0; i < contours.size(); i++) {
            const contour = contours.get(i);
            const area = cv.contourArea(contour);
            if (area < minDocArea || area <= bestArea) continue;

            const peri = cv.arcLength(contour, true);

            for (let epsilonFactor = 0.01; epsilonFactor <= 0.06; epsilonFactor += 0.005) {
              const approx = new cv.Mat();
              tempMats.push(approx);
              cv.approxPolyDP(contour, approx, epsilonFactor * peri, true);

              if (approx.rows === 4 && cv.isContourConvex(approx)) {
                const points = [];
                for (let j = 0; j < 4; j++) {
                  points.push({ x: approx.data32S[j * 2], y: approx.data32S[j * 2 + 1] });
                }
                bestPoints = points;
                bestArea = area;
                break;
              }
            }
          }

          contours.delete();
          hierarchy.delete();
          contours = null;
          hierarchy = null;

          // Good enough — stop trying looser thresholds
          if (bestArea > imageArea * 0.2) break;
        }

        if (bestPoints) {
          this.detectedCorners.set(bestPoints);
        } else {
          alert('Could not find a 4-cornered document outline.');
        }
    } catch (e) {
      console.error("[Edge Detection] An unexpected error occurred during OpenCV processing:", e);
      alert("An error occurred during image processing. See console for details.");
    } finally {
      console.log('[Edge Detection] Cleaning up OpenCV memory...');
      if (src) src.delete();
      if (gray) gray.delete();
      if (blurred) blurred.delete();
      if (canny) canny.delete();
      if (contours) contours.delete();
      if (hierarchy) hierarchy.delete();
      for (const mat of tempMats) {
        if (mat && !mat.isDeleted()) mat.delete();
      }
      console.log('[Edge Detection] Cleanup complete.');
    }
  }

  private drawCorners(points: {x: number, y: number}[]) {
    const canvas = this.docCanvas()?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    // Draw semi-transparent polygon fill
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(0, 123, 255, 0.12)';
    ctx.fill();
    ctx.strokeStyle = '#007bff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw corner handles
    const dragging = this.draggingCornerIndex();
    const hovered = this.hoveredCornerIndex();

    points.forEach((p, i) => {
        const isActive = dragging === i;
        const isHovered = hovered === i;
        const radius = isActive ? this.CORNER_DRAW_RADIUS + 3 : isHovered ? this.CORNER_DRAW_RADIUS + 2 : this.CORNER_DRAW_RADIUS;

        // White outer ring
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius + 2, 0, 2 * Math.PI);
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        // Blue fill
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = isActive ? '#0056b3' : isHovered ? '#3395ff' : '#007bff';
        ctx.fill();

        // White center dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, 2 * Math.PI);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
    });
  }

  applyCrop() {
    const corners = this.detectedCorners();
    if (!this.cvReady() || !corners) return;

    const canvas = this.docCanvas()?.nativeElement;
    if (!canvas) return;

    this.preCropImageSrc.set(this.originalImage()!.src);

    const sortedCorners = [...corners].sort((a, b) => a.y - b.y);
    const top = sortedCorners.slice(0, 2).sort((a, b) => a.x - b.x);
    const bottom = sortedCorners.slice(2).sort((a, b) => a.x - b.x);
    const [tl, tr] = top;
    const [bl, br] = bottom;

    const widthA = Math.hypot(br.x - bl.x, br.y - bl.y);
    const widthB = Math.hypot(tr.x - tl.x, tr.y - tl.y);
    const maxWidth = Math.max(widthA, widthB);

    const heightA = Math.hypot(tr.x - br.x, tr.y - br.y);
    const heightB = Math.hypot(tl.x - bl.x, tl.y - bl.y);
    const maxHeight = Math.max(heightA, heightB);

    const destWidth = Math.round(maxWidth);
    const destHeight = Math.round(maxHeight);
    
    let src, warped, srcTri, dstTri, M;
    try {
        // Redraw the clean image without corner overlays before reading pixels.
        this.drawAndFitImage();
        const cv = window.cv;
        src = cv.imread(canvas);
        warped = new cv.Mat();
        
        srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y]);
        dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, destWidth, 0, destWidth, destHeight, 0, destHeight]);
        
        M = cv.getPerspectiveTransform(srcTri, dstTri);
        cv.warpPerspective(src, warped, M, new cv.Size(destWidth, destHeight));

        cv.imshow(canvas, warped);

        const dataUrl = canvas.toDataURL();
        const croppedImage = new Image();
        croppedImage.onload = () => {
          this.originalImage.set(croppedImage);
        };
        croppedImage.src = dataUrl;

        this.isCropped.set(true);
        this.detectedCorners.set(null);
    } catch(e) {
      console.error("OpenCV crop error:", e);
      alert("An error occurred while cropping the image.");
    } finally {
        src?.delete();
        warped?.delete();
        srcTri?.delete();
        dstTri?.delete();
        M?.delete();
    }
  }

  undoCrop() {
    const src = this.preCropImageSrc();
    if (!src) return;
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      this.originalImage.set(img);
      this.isCropped.set(false);
      this.detectedCorners.set(null);
      this.preCropImageSrc.set(null);
    };
    img.src = src;
  }

  async saveCroppedDocument() {
    const doc = this.viewingDocument();
    const canvas = this.docCanvas()?.nativeElement;
    if (!doc || !canvas) return;

    this.isSaving.set(true);
    this.saveError.set(null);

    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b: Blob | null) => (b ? resolve(b) : reject('Failed to export image')),
          'image/png'
        );
      });
      const success = await this.supabaseService.saveCroppedDocument(doc, blob);
      if (success) {
        this.isCropped.set(false);
        this.preCropImageSrc.set(null);
        if (this.applicationId) {
          this.loadApplicationDetails(this.applicationId);
        }
      } else {
        this.saveError.set('Failed to save. Check storage permissions.');
      }
    } catch (e) {
      this.saveError.set('An unexpected error occurred.');
    } finally {
      this.isSaving.set(false);
    }
  }

  // --- Drag Interaction Helpers ---

  private getCanvasPoint(event: MouseEvent | Touch): {x: number, y: number} | null {
    const canvas = this.docCanvas()?.nativeElement;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  }

  private findCornerAtPoint(point: {x: number, y: number}): number | null {
    const corners = this.detectedCorners();
    if (!corners) return null;
    for (let i = 0; i < corners.length; i++) {
      const dx = corners[i].x - point.x;
      const dy = corners[i].y - point.y;
      if (Math.sqrt(dx * dx + dy * dy) <= this.CORNER_HIT_RADIUS) {
        return i;
      }
    }
    return null;
  }

  private redrawWithCorners(corners: {x: number, y: number}[]) {
    this.drawAndFitImage();
    this.drawCorners(corners);
  }

  // --- Manual Corner Selection ---

  startManualCornerSelection() {
    this.detectedCorners.set(null);
    this.manualCornerPoints.set([]);
    this.manualCornerMode.set(true);
    this.drawAndFitImage();
  }

  private placeManualCorner(point: {x: number, y: number}) {
    const points = [...this.manualCornerPoints(), point];
    this.manualCornerPoints.set(points);
    this.drawAndFitImage();

    if (points.length >= 4) {
      this.manualCornerMode.set(false);
      this.manualCornerPoints.set([]);
      this.detectedCorners.set(points);
    } else {
      this.drawPartialCorners(points);
    }
  }

  private drawPartialCorners(points: {x: number, y: number}[], cursor?: {x: number, y: number}) {
    const canvas = this.docCanvas()?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas || points.length === 0) return;

    // Dashed lines connecting placed points
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    // Preview line to cursor
    if (cursor) {
      ctx.lineTo(cursor.x, cursor.y);
    }
    ctx.strokeStyle = '#007bff';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Corner handles
    points.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, this.CORNER_DRAW_RADIUS + 2, 0, 2 * Math.PI);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p.x, p.y, this.CORNER_DRAW_RADIUS, 0, 2 * Math.PI);
      ctx.fillStyle = '#007bff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, 2 * Math.PI);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
    });
  }

  // --- Mouse Event Handlers ---

  onCanvasMouseDown(event: MouseEvent) {
    const point = this.getCanvasPoint(event);
    if (!point) return;

    if (this.manualCornerMode()) {
      this.placeManualCorner(point);
      return;
    }

    const index = this.findCornerAtPoint(point);
    if (index !== null) {
      this.draggingCornerIndex.set(index);
    }
  }

  onCanvasMouseMove(event: MouseEvent) {
    const point = this.getCanvasPoint(event);
    if (!point) return;

    if (this.manualCornerMode()) {
      const placed = this.manualCornerPoints();
      if (placed.length > 0) {
        this.drawAndFitImage();
        this.drawPartialCorners(placed, point);
      }
      return;
    }

    if (this.draggingCornerIndex() !== null) {
      const corners = this.detectedCorners();
      if (!corners) return;
      const canvas = this.docCanvas()?.nativeElement;
      if (!canvas) return;

      const updated = [...corners];
      updated[this.draggingCornerIndex()!] = {
        x: Math.max(0, Math.min(canvas.width, point.x)),
        y: Math.max(0, Math.min(canvas.height, point.y))
      };
      this.detectedCorners.set(updated);
      this.redrawWithCorners(updated);
    } else {
      const index = this.findCornerAtPoint(point);
      if (this.hoveredCornerIndex() !== index) {
        this.hoveredCornerIndex.set(index);
        const corners = this.detectedCorners();
        if (corners) {
          this.redrawWithCorners(corners);
        }
      }
    }
  }

  onCanvasMouseUp(_event: MouseEvent) {
    this.draggingCornerIndex.set(null);
  }

  onCanvasMouseLeave(_event: MouseEvent) {
    this.draggingCornerIndex.set(null);
    this.hoveredCornerIndex.set(null);
    const corners = this.detectedCorners();
    if (corners) {
      this.redrawWithCorners(corners);
    }
  }

  // --- Touch Event Handlers ---

  onCanvasTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) return;
    const point = this.getCanvasPoint(event.touches[0]);
    if (!point) return;

    if (this.manualCornerMode()) {
      event.preventDefault();
      this.placeManualCorner(point);
      return;
    }

    const index = this.findCornerAtPoint(point);
    if (index !== null) {
      event.preventDefault();
      this.draggingCornerIndex.set(index);
    }
  }

  onCanvasTouchMove(event: TouchEvent) {
    if (this.draggingCornerIndex() === null) return;
    event.preventDefault();
    const point = this.getCanvasPoint(event.touches[0]);
    if (!point) return;

    const corners = this.detectedCorners();
    if (!corners) return;
    const canvas = this.docCanvas()?.nativeElement;
    if (!canvas) return;

    const updated = [...corners];
    updated[this.draggingCornerIndex()!] = {
      x: Math.max(0, Math.min(canvas.width, point.x)),
      y: Math.max(0, Math.min(canvas.height, point.y))
    };
    this.detectedCorners.set(updated);
    this.redrawWithCorners(updated);
  }

  onCanvasTouchEnd(_event: TouchEvent) {
    this.draggingCornerIndex.set(null);
  }

  private drawAndFitImage() {
    const canvas = this.docCanvas()?.nativeElement;
    const originalImage = this.originalImage();
    const ctx = canvas?.getContext('2d');

    if (!ctx || !originalImage || !canvas.parentElement) return;

    const container = canvas.parentElement;
    const maxWidth = container.clientWidth;
    const maxHeight = container.clientHeight;
    
    if (maxWidth === 0 || maxHeight === 0) return;

    const imgRatio = originalImage.naturalWidth / originalImage.naturalHeight;
    const containerRatio = maxWidth / maxHeight;

    let targetWidth: number;
    let targetHeight: number;

    if (imgRatio > containerRatio) {
      targetWidth = maxWidth;
      targetHeight = targetWidth / imgRatio;
    } else {
      targetHeight = maxHeight;
      targetWidth = targetHeight * imgRatio;
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    ctx.drawImage(originalImage, 0, 0, targetWidth, targetHeight);
  }

  resetImage() {
    const originalImage = this.originalImage();
    if (!originalImage) return;

    // To re-trigger the effect that draws the image, we create a new Image object.
    // This ensures the canvas is redrawn with the original, unfitted image data.
    const newImage = new Image();
    newImage.crossOrigin = 'Anonymous';
    newImage.onload = () => {
        this.originalImage.set(newImage);
        this.resetImageState();
    };
    newImage.src = originalImage.src;
  }

  private resetImageState() {
    this.detectedCorners.set(null);
    this.isCropped.set(false);
    this.draggingCornerIndex.set(null);
    this.hoveredCornerIndex.set(null);
    this.manualCornerMode.set(false);
    this.manualCornerPoints.set([]);
    this.preCropImageSrc.set(null);
    this.isSaving.set(false);
    this.saveError.set(null);
  }



  async revertApprovedApplication() {
    const reviewer = this.reviewer();
    if (!this.applicationId || !reviewer || !this.isBuildFailed()) return;

    this.revertingApproved.set(true);
    try {
      await this.supabaseService.revertApprovedToSubmitted(this.applicationId);
      // Log is written by the RPC; local state will refresh via realtime subscription
      this.validationStatus.set('idle');
      this.validationError.set(null);
    } catch (e: any) {
      this.error.set(e.message || 'Failed to revert application.');
      console.error('Error reverting approved application:', e);
    } finally {
      this.revertingApproved.set(false);
    }
  }

  promptSendLegalDocuments() {
    this.sendLegalDocsSuccess.set(false);
    this.sendLegalDocsError.set(null);
    this.showLegalDocsConfirm.set(true);
  }

  cancelSendLegalDocuments() {
    this.showLegalDocsConfirm.set(false);
  }

  async confirmSendLegalDocuments() {
    if (!this.applicationId || this.sendingLegalDocs()) return;

    this.sendingLegalDocs.set(true);
    this.sendLegalDocsError.set(null);

    try {
      await this.supabaseService.sendLegalDocuments(this.applicationId);
      this.showLegalDocsConfirm.set(false);
      this.sendLegalDocsSuccess.set(true);
      setTimeout(() => this.sendLegalDocsSuccess.set(false), 5000);
    } catch (e: any) {
      this.sendLegalDocsError.set(e.message || 'Failed to send legal documents.');
      console.error('Error sending legal documents:', e);
      setTimeout(() => this.sendLegalDocsError.set(null), 8000);
    } finally {
      this.sendingLegalDocs.set(false);
    }
  }
}
